/**
 * Hooks for the SAFE assessments.
 *
 * The split here follows the API's own split, and it is the thing to understand before reading
 * the components:
 *
 *  - **Reference data is cached.** Collections, the architect priority table and the diagnostics
 *    are read-only server state, so they go through react-query with a long stale time. They
 *    change when an administrator edits the mappings, not during a workshop.
 *  - **The assessment is not cached.** `useSafeSession` holds it in component state instead. The
 *    session endpoint is stateless (see `support/safe/safeApi.ts`): the client owns the workshop
 *    document and the server returns a computed *view* of whatever was posted. Keying that by
 *    query key would mean caching a derived value against an input that is not in the key, and
 *    react-query would happily serve a stale assessment after an edit.
 *
 * `useSafeSession` is therefore a small state machine rather than a query:
 *
 *      GET db/workshop/<id>/state/   ->  the stored document
 *          |
 *          v
 *      workshop (client-owned)
 *          |  applyEdits([...])
 *          v
 *      POST db/safe/session/  ->  { summary, capabilities, badges, ..., workshop }
 *          |
 *          +-> store the returned workshop; it is now the document
 *          +-> PUT db/workshop/<id>/state/  (and cache it locally)
 *
 * **An assessment always belongs to a workshop.** `workshopId` is required, and it is where the
 * document is read from and written to — that is what makes an assessment resumable by a colleague,
 * recoverable from another machine and attributable to a Salesforce Account. The scoring endpoint
 * stays stateless; persistence is a separate call to a separate endpoint that has no opinion about
 * what the state means.
 *
 * Two failure modes are handled differently on purpose:
 *
 *  - **The state cannot be read.** The hook refuses to start rather than opening on defaults, since
 *    computing a default document and saving it would overwrite the workshop it failed to read.
 *  - **The state cannot be written.** The assessment carries on and `saveError` is set, because the
 *    work in the page is still good and a mid-workshop hard stop would be worse than a warning. The
 *    local cache holds the unsaved document so a reload can push it up (see `safeWorkshop.ts`).
 *
 * Edits are serialised. A workshop is a single document, so two in-flight POSTs would each apply
 * their edits to the *same* pre-edit workshop and whichever landed second would silently discard
 * the other's change. Clicking three coverage toggles quickly is completely ordinary in a
 * workshop, so the queue below is not a nicety — and the same queue serialises the writes, so the
 * document that lands on the server is never an older one overtaking a newer.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query'; //-tech-debt-react-query;
import { CacheKey } from '../types/cache-keys';
import {
  SafeCapabilityPriority,
  SafeCollectionsResponse,
  SafeDiagnostics,
  SafeEdit,
  SafeInclude,
  SafeMode,
  SafeSessionResponse,
  SafeWorkshop,
  getSafeCapabilityPriority,
  getSafeCollections,
  getSafeDiagnostics,
  importSafeAssessment,
  postSafeSession,
} from '../support/safe/safeApi';
import {
  clearWorkshopCache,
  loadWorkshopCache,
  saveWorkshopCache,
} from '../support/safe/safeWorkshop';
import {
  getWorkshopState,
  putWorkshopState,
} from '../support/workshops/workshopApi';

/** Reference data changes only when an admin edits it, so an hour is generous but not stale. */
const REFERENCE_STALE_TIME = 60 * 60 * 1000;

export function useSafeCollections(
  options: { includeHidden?: boolean; enabled?: boolean } = {},
): UseQueryResult<SafeCollectionsResponse> {
  const { includeHidden, enabled = true } = options;

  return useQuery({
    queryKey: [CacheKey.SafeCollections, includeHidden ?? null],
    queryFn: () => getSafeCollections({ includeHidden }),
    ...{ enabled, staleTime: REFERENCE_STALE_TIME },
  });
}

export function useSafeCapabilityPriority(
  options: { enabled?: boolean } = {},
): UseQueryResult<SafeCapabilityPriority> {
  return useQuery({
    queryKey: [CacheKey.SafeCapabilityPriority],
    queryFn: getSafeCapabilityPriority,
    ...{
      enabled: options.enabled ?? true,
      staleTime: REFERENCE_STALE_TIME,
    },
  });
}

/**
 * Whether this database can answer an assessment correctly.
 *
 * Shown in Management > SAFE rather than in the assessment: an empty `business_technology` table
 * produces a perfectly valid, entirely empty workshop, and the person who can fix that is the
 * administrator, not the SE mid-workshop.
 */
export function useSafeDiagnostics(
  options: { enabled?: boolean } = {},
): UseQueryResult<SafeDiagnostics> {
  return useQuery({
    queryKey: [CacheKey.SafeDiagnostics],
    queryFn: getSafeDiagnostics,
    ...{
      enabled: options.enabled ?? true,
      staleTime: 5 * 60 * 1000,
    },
  });
}

/**
 * The architectures the Capability Selection filter can narrow to, and their capabilities.
 *
 * Only the session endpoint serves this, but the list does not depend on the workshop — so it is
 * fetched once with no workshop and cached like the other reference data, rather than added to
 * `include` on the shared session where it would be rebuilt on every coverage toggle.
 */
export function useSafeArchitectures(
  options: { enabled?: boolean } = {},
): UseQueryResult<NonNullable<SafeSessionResponse['architecture']>> {
  return useQuery({
    queryKey: [CacheKey.SafeArchitectures],
    queryFn: async () => {
      const result = await postSafeSession({ include: ['architecture'] });
      return result.architecture || [];
    },
    ...{ enabled: options.enabled ?? true, staleTime: REFERENCE_STALE_TIME },
  });
}

/**
 * The diagram's own view of the workshop: pin badges and flow settings.
 *
 * Split out from `useSafeSession` on purpose. `badges` and `flow` are the two expensive sections
 * to compute, and only one of the five steps shows them — folding them into the shared session
 * would rebuild the whole diagram on every coverage toggle on the capability step.
 *
 * Keyed on the workshop object's identity, which changes on every commit, so the diagram follows
 * edits without polling.
 */
export function useSafeFlowView(
  mode: SafeMode,
  workshop: SafeWorkshop | null,
): {
  flow: SafeSessionResponse['flow'] | null;
  badges: SafeSessionResponse['badges'] | null;
  loading: boolean;
  error: string;
} {
  const [flow, setFlow] = useState<SafeSessionResponse['flow'] | null>(null);
  const [badges, setBadges] = useState<SafeSessionResponse['badges'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!workshop) return;

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError('');

      try {
        const result = await postSafeSession({
          mode,
          workshop,
          include: ['flow', 'badges'],
        });

        if (cancelled) return;

        setFlow(result.flow || null);
        setBadges(result.badges || null);
      } catch (err: any) {
        if (!cancelled) setError(String(err?.message || 'The diagram could not be built.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [mode, workshop]);

  return { flow, badges, loading, error };
}

export type UseSafeSessionOptions = {
  mode: SafeMode;
  /**
   * The workshop this assessment belongs to. Required: an assessment with nowhere to be stored is
   * the thing these tables exist to stop happening.
   */
  workshopId: number;
  /** Response sections to request. Adding `badges`/`flow` costs real work server-side. */
  include?: SafeInclude[];
};

export type UseSafeSessionResult = {
  session: SafeSessionResponse | null;
  workshop: SafeWorkshop | null;
  /** True until the stored state has been read and the first response has landed. */
  loading: boolean;
  /** True while an edit is in flight; the previous session stays readable throughout. */
  saving: boolean;
  /**
   * A blocking failure: the stored state could not be read, or the assessment could not be
   * computed. When this is set and `session` is null, there is nothing to show.
   */
  error: string;
  /**
   * The last write to the server failed. The assessment in the page is still good and still
   * editable — this is a warning to surface, not a reason to stop.
   */
  saveError: string;
  /** When the document was last stored on the server. */
  savedAt: Date | null;
  /** Server-side warnings from the last call (e.g. a custom capability that will not export). */
  warnings: string[];
  /** Queue one or more edits. Ordering within a call is preserved and it matters. */
  applyEdits: (edits: SafeEdit[]) => void;
  /** Re-request the current workshop unchanged. */
  refresh: () => void;
  /** Replace the workshop from an imported assessment file. */
  importAssessment: (assessment: any) => Promise<SafeSessionResponse>;
  /** Discard the assessment — the stored state and the local copy — and start from defaults. */
  reset: () => void;
};

export function useSafeSession(options: UseSafeSessionOptions): UseSafeSessionResult {
  const { mode, workshopId, include } = options;

  const [session, setSession] = useState<SafeSessionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // The workshop is held in a ref as well as in state because the edit queue reads it outside
  // React's render cycle: a queued edit that closed over a stale `workshop` would post the
  // pre-edit document and undo the edit before it.
  const workshopRef = useRef<SafeWorkshop | null>(null);
  const [workshop, setWorkshop] = useState<SafeWorkshop | null>(null);

  const pendingRef = useRef<SafeEdit[]>([]);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  /** Whether a session has come back at least once for the current workshop. */
  const loadedRef = useRef(false);

  /** Whether the stored state has been read. Until it has, nothing may be written. */
  const stateReadRef = useRef(false);

  /**
   * The document as the server last had it, serialised, or null for a workshop with no state.
   *
   * Compared against each computed document to decide whether a write is needed. Without it every
   * response would be written back — including the one produced by merely opening a workshop, which
   * would flip a `draft` engagement to `in_progress` for a read.
   */
  const storedJsonRef = useRef<string | null>(null);

  // `workshop` is forced into every request regardless of what the caller asked for. The client
  // owns the document, so a response without it would leave this hook with nothing to post next
  // time — every subsequent edit would be applied to server defaults instead.
  //
  // `include` is usually an inline array literal, which is a new identity every render. Joining
  // the normalised list gives a stable dependency so the initial-load effect does not re-fire
  // forever.
  const includeKey = Array.from(new Set([...(include || []), 'workshop'])).join(',');

  const includeList = useMemo<SafeInclude[]>(
    () => includeKey.split(',') as SafeInclude[],
    [includeKey],
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const commit = useCallback(
    (result: SafeSessionResponse) => {
      if (!mountedRef.current) return;

      setSession(result);

      if (result.workshop) {
        workshopRef.current = result.workshop;
        setWorkshop(result.workshop);

        // Cached before the server write, not after: the point of the cache is to hold a document
        // whose write has not landed.
        saveWorkshopCache(workshopId, result.workshop);
      }
    },
    [workshopId],
  );

  /**
   * Read the stored document for this workshop.
   *
   * Prefers the local cache when it is newer than the server's copy. In the ordinary case the two
   * are written together and identical, so this only ever matters when a save failed — and in that
   * case the cache is the work the SE actually did. Clock skew cannot make this lose anything for
   * the same reason: a cache "newer" than an identical server copy resolves to the same document.
   */
  const readStoredState = useCallback(async (): Promise<SafeWorkshop | null> => {
    const stored = await getWorkshopState(workshopId);

    const server =
      stored.state && Object.keys(stored.state).length ? (stored.state as SafeWorkshop) : null;
    const serverUpdated = stored.progress?.stateUpdated
      ? new Date(stored.progress.stateUpdated)
      : null;

    storedJsonRef.current = server ? JSON.stringify(server) : null;

    if (mountedRef.current) setSavedAt(serverUpdated);

    const cached = loadWorkshopCache(workshopId);

    if (cached && (!serverUpdated || cached.savedAt.getTime() > serverUpdated.getTime())) {
      // Left as the *server's* document in `storedJsonRef` on purpose, so the recovered copy is
      // written back on the first pass rather than being mistaken for what is already stored.
      return cached.workshop;
    }

    return server;
  }, [workshopId]);

  /**
   * Store the document.
   *
   * `PUT`, so a store the SE emptied actually disappears; the client owns the whole document. A
   * failure here is reported and swallowed — see the module comment on the two failure modes.
   */
  const persistState = useCallback(
    async (document: SafeWorkshop | null) => {
      if (!document) return;

      const serialised = JSON.stringify(document);

      // Nothing changed since the last successful write, so this is a read of the workshop and not
      // an edit to it. Writing anyway would move `last_updated` and start a draft workshop.
      if (serialised === storedJsonRef.current) return;

      try {
        await putWorkshopState(workshopId, document);
        storedJsonRef.current = serialised;

        if (mountedRef.current) {
          setSavedAt(new Date());
          setSaveError('');
        }
      } catch (err: any) {
        if (mountedRef.current) {
          setSaveError(
            String(err?.message || 'This assessment could not be saved to the workshop.'),
          );
        }
      }
    },
    [workshopId],
  );

  /**
   * Drain the edit queue, one request at a time.
   *
   * Everything queued while a request is in flight is taken in the *next* request, in order, so
   * a burst of clicks becomes one or two round trips rather than one per click — and no edit is
   * ever applied to a workshop that has since been superseded.
   */
  const drain = useCallback(async () => {
    if (inFlightRef.current) return;

    inFlightRef.current = true;

    try {
      // The stored document is read here rather than in the effect below so that a failure to read
      // it stops the whole cycle: computing a session from defaults and saving it would replace the
      // state this just failed to fetch.
      if (!stateReadRef.current) {
        workshopRef.current = await readStoredState();
        stateReadRef.current = true;

        if (mountedRef.current) setWorkshop(workshopRef.current);
      }

      // `loadedRef` rather than `!workshopRef.current`: the very first pass has to run even with
      // an empty queue (to fetch the computed view of the stored or default workshop), but once it
      // has run, an empty queue means there is nothing to do.
      while (pendingRef.current.length || !loadedRef.current) {
        const edits = pendingRef.current;
        pendingRef.current = [];

        setSaving(loadedRef.current);

        const result = await postSafeSession({
          mode,
          workshop: workshopRef.current,
          edits: edits.length ? edits : undefined,
          include: includeList,
        });

        loadedRef.current = true;
        commit(result);

        if (mountedRef.current) {
          setError('');
          setLoading(false);
        }

        // Awaited inside the queue rather than fired off, so writes cannot overtake each other and
        // leave an older document as the stored one. `persistState` decides whether there is
        // anything to write: reopening a workshop and changing nothing writes nothing, while the
        // *first* open of one with no state writes the default document — which is the right
        // moment for a draft engagement to become one in progress.
        await persistState(result.workshop || workshopRef.current);
      }
    } catch (err: any) {
      if (mountedRef.current) {
        setError(
          String(
            err?.message ||
              (stateReadRef.current
                ? 'The SAFE assessment could not be updated.'
                : 'This workshop’s saved assessment could not be read.'),
          ),
        );
        setLoading(false);
      }

      // Dropped rather than retried. A rejected edit is usually a 400 naming a bad id, and
      // replaying it would fail identically on every subsequent change.
      pendingRef.current = [];
    } finally {
      inFlightRef.current = false;

      if (mountedRef.current) setSaving(false);

      // Something arrived during the await; go round again.
      if (pendingRef.current.length) void drain();
    }
  }, [mode, includeList, commit, readStoredState, persistState]);

  // Initial load: read the workshop's stored state, then compute a session from it. Changing
  // workshop or mode starts the cycle again from the store rather than from whatever is in memory.
  useEffect(() => {
    workshopRef.current = null;
    stateReadRef.current = false;
    loadedRef.current = false;
    storedJsonRef.current = null;
    pendingRef.current = [];

    setWorkshop(null);
    setSession(null);
    setLoading(true);
    setError('');
    setSaveError('');
    setSavedAt(null);

    void drain();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, workshopId, includeKey]);

  const applyEdits = useCallback(
    (edits: SafeEdit[]) => {
      if (!edits?.length) return;

      pendingRef.current = [...pendingRef.current, ...edits];
      void drain();
    },
    [drain],
  );

  /** Re-post the current workshop unchanged, e.g. after a new `include` section was requested. */
  const refresh = useCallback(() => {
    loadedRef.current = false;
    void drain();
  }, [drain]);

  const importAssessment = useCallback(
    async (assessment: any) => {
      setSaving(true);
      setError('');

      try {
        const result = await importSafeAssessment(assessment, {
          mode,
          include: includeList,
        });

        commit(result);

        // An import replaces the assessment, so it is stored immediately rather than waiting for
        // the next edit — otherwise a reload would resurrect the document the file just replaced.
        await persistState(result.workshop || null);

        return result;
      } catch (err: any) {
        const message = String(err?.message || 'That assessment could not be imported.');
        if (mountedRef.current) setError(message);
        throw new Error(message);
      } finally {
        if (mountedRef.current) setSaving(false);
      }
    },
    [mode, includeList, commit, persistState],
  );

  /**
   * Throw the assessment away and start from defaults.
   *
   * The workshop itself survives — its customer, type, owner and delivery progress are not the
   * assessment. What is discarded is the state document, locally and on the server: `stateReadRef`
   * stays true so the cycle does not read back the copy it is deleting, and the defaults the next
   * session computes are written in its place.
   */
  const reset = useCallback(() => {
    clearWorkshopCache(workshopId);

    workshopRef.current = null;
    pendingRef.current = [];
    loadedRef.current = false;
    stateReadRef.current = true;
    storedJsonRef.current = null;

    setWorkshop(null);
    setSession(null);
    setLoading(true);
    setError('');

    void drain();
  }, [workshopId, drain]);

  return {
    session,
    workshop,
    loading,
    saving,
    error,
    saveError,
    savedAt,
    warnings: session?.warnings || [],
    applyEdits,
    refresh,
    importAssessment,
    reset,
  };
}
